import React, {useState, useEffect, useRef} from "react";
import Periodicity from "../periodicity/Periodicity.jsx";
import ItemOptionsModal from "../item-options-modal/ItemOptionModal";
import { del, put } from "../../util/fetch";
import classNames from "classnames";
import LoadingSpinner from "../../atoms/loadingSpinner/LoadingSpinner";
import Close from "jsx:../../assets/svgs/close.svg";
import UpcomingDatesIndicator from "./UpcomingDatesIndicator";

export default function RuleProduct({
  item,
  handleQts,
  logoSmallUrl,
  delivery,
  fetchPreferencesAndRules,
  fetchCatalog,
  fetchDelivery,
  timeslotsBeforeOrdering,
}) {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [fromSettingsRemoveRule, setFromSettingsRemoveRule] = useState(false);
  const [isLoadingRemoveRule, setIsLoadingRemoveRule] = useState(false);

  const [deletingRuleId, setDeletingRuleId] = useState(null);
  const [upcomingDates, setUpcomingDates] = useState([]);
  const [isLoadingUpcomingDates, setIsLoadingUpcomingDates] = useState(false);

  const display_amount = item ? item.temporary_amount : null;
  const isTemporary = item ? item.is_temporary : null;
  const periodicity = item ? item.periodicity : null;
  const imageUrl = item?.image ? item.image : logoSmallUrl;
  const isUnavailable = item?.availability !== 2;

  const ruleDeleted = useRef(false);
  const mounted = useRef(true);

  useEffect(() => {
    return () => {
      mounted.current = false;
    };
  }, []);

  useEffect(() => {
    if (!ruleDeleted.current) {
      fetchUpcomingDates();
    }
  }, [item, delivery, timeslotsBeforeOrdering]);

  const fetchUpcomingDates = async () => {
    if (!item || !item.id || timeslotsBeforeOrdering === undefined) return;

    if (mounted.current) {
      setIsLoadingUpcomingDates(true);
      setUpcomingDates([]);
    }

    try {
      const deliveryId = delivery.id;
      const subscriptionId = delivery.subscription.id;
      let ruleProductUrl = `/api/rule-product/upcoming_dates/?delivery_id=${deliveryId}&subscription_id=${subscriptionId}&rule_id=${item.id}`;

      if (timeslotsBeforeOrdering !== undefined) {
        ruleProductUrl += `&timeslots=${encodeURIComponent(
          JSON.stringify(timeslotsBeforeOrdering),
        )}`;
      }

      const response = await fetch(ruleProductUrl);
      if (!response.ok) {
        throw new Error(response.statusText);
      }

      const data = await response.json();
      if (mounted.current) {
        setUpcomingDates(data.dates);
      }
    } catch (error) {
      console.error("Error fetching upcoming dates:", error);
      if (mounted.current) {
        setUpcomingDates([]);
      }
    } finally {
      if (mounted.current) {
        setIsLoadingUpcomingDates(false);
      }
    }
  };

  const handleOpenModal = () => {
    if (!isTemporary && !isUnavailable) {
      setIsModalOpen(true);
    }
  };

  const deleteRule = (ruleId) => {
    const apiUrl = "/api/rule-product/" + (ruleId ? ruleId + "/" : "");
    return del(apiUrl);
  };

  const refreshData = () => {
    fetchPreferencesAndRules(delivery.subscription.id, true);
    fetchCatalog();
    fetchDelivery();
  };

  const handleRuleDeletion = (ruleId) => {
    setDeletingRuleId(ruleId);
    setIsLoadingRemoveRule(true);

    return deleteRule(ruleId)
      .then(() => {
        refreshData();

        if (mounted.current) {
          setIsLoadingRemoveRule(false);
          setDeletingRuleId(null);
        }
      },
      (error) => {
        console.error(error);
        if (mounted.current) {
          setIsLoadingRemoveRule(false);
          setDeletingRuleId(null);
        }
      },
    );
  };

  const updateRuleProductData = (ruleProductData) => {
    const ruleId = ruleProductData["rule_id"];
    const apiUrl = "/api/rule-product/" + (ruleId ? ruleId + "/" : "");

    ruleProductData.temporary_amount = ruleProductData.totalAmount;
    ruleProductData.amount = ruleProductData.totalAmount;

    delete ruleProductData.totalAmount;
    delete ruleProductData.is_temporary;
    delete ruleProductData.listing;

    return put(apiUrl, { body: ruleProductData }).then(() => {
      refreshData();
    });
  };

  const handleRemoveIconClick = (e) => {
    e.stopPropagation();
    setFromSettingsRemoveRule(true);
    setIsModalOpen(true);
  };

  const handleCardBodyClick = (e) => {
    if (!isTemporary && !isUnavailable) {
      handleOpenModal();
    }
  };

  const handleConfirmModal = (ruleProductData) => {
    const handleError = (error) => {
      console.error(error);
    };

    const ruleId = ruleProductData["rule_id"];
    const isOnce = ruleProductData.periodicity === "ONCE";

    if (isOnce) {
      if (ruleId) {
        ruleDeleted.current = true;
        setDeletingRuleId(item.id);

        handleRuleDeletion(ruleId).then(() => {
          setIsModalOpen(false);
        }, handleError);
      } else {
        setIsModalOpen(false);
      }
    } else {
      updateRuleProductData(ruleProductData).then(() => {
        setIsModalOpen(false);
      }, handleError);
    }
  };

  return (
    <div className="rule__proposal_card">
      {isLoadingRemoveRule && deletingRuleId === item.id ? (
        <LoadingSpinner
          isLoading={true}
          clazzName="rule__loading_icon"
        />
      ) : (
        <Close
          className={classNames("close-icon-cart")}
          onClick={handleRemoveIconClick}
        />
      )}

      <div
        className="rule__proposal_card-body"
        onClick={handleCardBodyClick}
        style={!isTemporary && !isUnavailable ? { cursor: 'pointer' } : {}}
      >
        {/* Column 1: Image */}
        <div className="rule__proposal_card-img-container">
          <img className="rule__proposal_card-image" src={imageUrl} alt="img" />
        </div>

        {/* Column 2: Title */}
        <div className="rule__proposal_card-title">
          <div>
            {item.article_name_and_qualities}
            {isUnavailable && (
              <span className="rule__proposal_card-unavailable">
                unverfügbar
              </span>
            )}
          </div>
          <div>{item.producer}</div>
        </div>

        {/* Column 3: Dots */}

        <UpcomingDatesIndicator
          dates={upcomingDates}
          isLoading={isLoadingUpcomingDates}
        />

        {/* Column 4: Quantity */}
        <div className="rule__proposal_card-quantity-container">
          <div
            className={`rule__proposal_card-quantity ${
              isTemporary ? "disabled" : ""
            }`}
          >
            {item.weight_in_milli ? (
              <span>{item.rule_amount_weight_display}</span>
            ) : (
              <div>
                <span>{display_amount}</span>
                <span className="rule__proposal_card-unit">
                  {" "}
                  x {item.rule_amount_unit_display}
                </span>
              </div>
            )}
          </div>
        </div>

        {/* Column 5: Periodicity */}
        <Periodicity
          periodicity={periodicity}
          editable={!isTemporary && !isUnavailable}
        />
      </div>

      {isModalOpen && (
        <ItemOptionsModal
          deliver_listing={item}
          delivery={delivery}
          fromSettingsRemoveRule={fromSettingsRemoveRule}
          onClose={() => {
            setFromSettingsRemoveRule(false);
            setIsModalOpen(false);
          }}
          onConfirm={handleConfirmModal}
          rule={item}
          fromSettings={true}
          isUnavailable={isUnavailable}
        />
      )}
    </div>
  );
}
